import React, { useState } from 'react';
import { AlertTriangle, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { produce } from 'immer';
import { generateUUID } from '../lib/scriptHelpers';
import StepVarEditor from './StepVarEditor';

export default function IncidentsEditor({ incidents, onChange }) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedSteps, setExpandedSteps] = useState({});

  const incidentGroups = incidents?.Incident_Groups || [];

  const updateIncidents = (updater) => {
    const newIncidents = produce(incidents || { Incident_Groups: [] }, updater);
    onChange(newIncidents);
  };

  const addGroup = () => {
    updateIncidents(draft => {
      if (!draft.Incident_Groups) draft.Incident_Groups = [];
      const groupNum = draft.Incident_Groups.length + 1;
      draft.Incident_Groups.push({
        UUID: generateUUID('incident'),
        Name_ID: `I_ALT_${groupNum}`,
        Incident_Number: `I${groupNum}`,
        Incident_Name: `Новый инцидент ${groupNum}`,
        Choose_If: '',
        Steps: []
      });
    });
  };

  const deleteGroup = (index) => {
    if (!window.confirm('Удалить группу инцидентов?')) return;
    updateIncidents(draft => {
      draft.Incident_Groups.splice(index, 1);
    });
  };

  const addStep = (groupIndex) => {
    updateIncidents(draft => {
      const group = draft.Incident_Groups[groupIndex];
      if (!group.Steps) group.Steps = [];
      const stepNum = group.Steps.length + 1;
      group.Steps.push({
        UUID: generateUUID('incident-step'),
        Name_ID: `${group.Name_ID}_Step${stepNum}`,
        Step_Name: `Шаг ${stepNum}`,
        Step_Number: String(stepNum),
        Skip_If: '',
        Step_Description: '',
        Step_Vars: []
      });
    });
  };

  const deleteStep = (groupIndex, stepIndex) => {
    if (!window.confirm('Удалить шаг?')) return;
    updateIncidents(draft => {
      draft.Incident_Groups[groupIndex].Steps.splice(stepIndex, 1);
    });
  };

  const addStepVar = (groupIndex, stepIndex) => {
    updateIncidents(draft => {
      const step = draft.Incident_Groups[groupIndex].Steps[stepIndex];
      if (!step.Step_Vars) step.Step_Vars = [];
      const varNum = step.Step_Vars.length + 1;
      step.Step_Vars.push({
        Name_ID: `${step.Name_ID}_V${varNum}`,
        Var_UUID: generateUUID('incident-var'),
        Choose_If: '',
        Step_Var_Name: `Вариант ${varNum}`,
        Task_Settings: {
          Task: '',
          Status: '',
          Qual_Tags: [],
          Templates: [],
          Modul_Tags: [],
          Time_Wait_CL_Reply: '',
          Group_Followup_Or_Stop_If_No_Reply: ''
        }
      });
    });
  };

  const updateField = (path, value) => {
    updateIncidents(draft => {
      const keys = path.split('.');
      let current = draft;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-orange-100 rounded-lg">
          <AlertTriangle className="text-orange-600" size={24} />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-gray-900">Скрипты для инцидентов</h3>
          <p className="text-sm text-gray-600">Обработка особых ситуаций в диалоге</p>
        </div>
        <motion.button
          onClick={addGroup}
          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={16} />
          <span>Добавить инцидент</span>
        </motion.button>
      </div>

      <div className="space-y-4">
        <AnimatePresence>
          {incidentGroups.map((group, groupIndex) => (
            <motion.div
              key={group.UUID}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white border-2 border-orange-200 rounded-xl p-5 shadow-sm"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3 flex-1">
                  <button
                    onClick={() => setExpandedGroups(prev => ({ ...prev, [groupIndex]: !prev[groupIndex] }))}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    {expandedGroups[groupIndex] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </button>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={group.Incident_Name || ''}
                      onChange={(e) => updateField(`Incident_Groups.${groupIndex}.Incident_Name`, e.target.value)}
                      className="text-lg font-bold text-gray-900 border-2 border-transparent hover:border-orange-300 focus:border-orange-500 rounded-lg px-3 py-1 w-full"
                      placeholder="Название инцидента"
                    />
                    <span className="text-sm text-gray-500 ml-3">#{group.Incident_Number}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => addStep(groupIndex)}
                    className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                    title="Добавить шаг"
                  >
                    <Plus size={18} />
                  </button>
                  <button
                    onClick={() => deleteGroup(groupIndex)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Удалить инцидент"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {expandedGroups[groupIndex] && (
                <div className="space-y-3 mt-4">
                  <textarea
                    value={group.Choose_If || ''}
                    onChange={(e) => updateField(`Incident_Groups.${groupIndex}.Choose_If`, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    rows={2}
                    placeholder="Условие срабатывания инцидента (Choose_If)..."
                  />

                  {group.Steps?.map((step, stepIndex) => {
                    const stepKey = `${groupIndex}-${stepIndex}`;
                    return (
                      <div key={step.UUID} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <button
                            onClick={() => setExpandedSteps(prev => ({ ...prev, [stepKey]: !prev[stepKey] }))}
                            className="flex items-center gap-2 text-sm font-medium"
                          >
                            {expandedSteps[stepKey] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            <span>Шаг {step.Step_Number}: {step.Step_Name}</span>
                          </button>
                          <div className="flex gap-1">
                            <button
                              onClick={() => addStepVar(groupIndex, stepIndex)}
                              className="p-1 text-orange-600 hover:bg-orange-100 rounded"
                              title="Добавить вариант"
                            >
                              <Plus size={14} />
                            </button>
                            <button
                              onClick={() => deleteStep(groupIndex, stepIndex)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Удалить шаг"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {expandedSteps[stepKey] && (
                          <div className="mt-3 space-y-2">
                            <input
                              type="text"
                              value={step.Step_Name || ''}
                              onChange={(e) => updateField(`Incident_Groups.${groupIndex}.Steps.${stepIndex}.Step_Name`, e.target.value)}
                              className="w-full px-2 py-1 text-sm border rounded"
                              placeholder="Название шага"
                            />
                            <textarea
                              value={step.Step_Description || ''}
                              onChange={(e) => updateField(`Incident_Groups.${groupIndex}.Steps.${stepIndex}.Step_Description`, e.target.value)}
                              className="w-full px-2 py-1 text-xs border rounded"
                              rows={2}
                              placeholder="Описание шага"
                            />

                            {step.Step_Vars?.map((stepVar, varIndex) => (
                              <StepVarEditor
                                key={stepVar.Var_UUID}
                                stepVar={stepVar}
                                onChange={(newStepVar) => {
                                  updateIncidents(draft => {
                                    draft.Incident_Groups[groupIndex].Steps[stepIndex].Step_Vars[varIndex] = newStepVar;
                                  });
                                }}
                                onDelete={() => {
                                  updateIncidents(draft => {
                                    draft.Incident_Groups[groupIndex].Steps[stepIndex].Step_Vars.splice(varIndex, 1);
                                  });
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {incidentGroups.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white border-2 border-dashed border-gray-300 rounded-xl">
            Нет инцидентов. Нажмите "Добавить инцидент" для создания.
          </div>
        )}
      </div>
    </div>
  );
}
